import React from 'react';
import { useLocation } from 'react-router-dom';
import { isAdminHost } from '../admin-panel/config/adminHost';
import { hasPendingImpersonationHandoff } from '../impersonation/utils/impersonationHandoff';
import { isImpersonationHandoffActive } from '../impersonation/utils/impersonationSession';
import ComingSoonPage from './ComingSoonPage';
import { useComingSoon } from './ComingSoonContext';

const BYPASS_PREFIXES = [
  '/coming-soon-preview',
  '/__/auth',
  '/app/verify-email',
  '/app/reset-password',
  '/app/recover-email',
  '/app/magic-link',
  '/main/verify-email',
];

function isImpersonationLanding(pathname) {
  return pathname === '/app/impersonate' || pathname.startsWith('/app/impersonate/');
}

function shouldBypassComingSoon(pathname) {
  if (BYPASS_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`))) {
    return true;
  }
  // An active admin impersonation session, including the landing page that
  // claims the ticket, should reach the app instead of the coming soon gate.
  if (isImpersonationHandoffActive()) return true;
  if (isImpersonationLanding(pathname) && hasPendingImpersonationHandoff()) return true;
  return false;
}

export default function ComingSoonGuard({ children }) {
  const { enabled, unlocked, loading, unlockWithCode, unlockPreview } = useComingSoon();
  const { pathname } = useLocation();

  if (pathname === '/coming-soon-preview') {
    return <ComingSoonPage preview onUnlock={unlockPreview} />;
  }

  // The admin panel host must never sit behind the public coming soon gate,
  // even if a cached "enabled" flag would otherwise paint that screen first.
  if (isAdminHost()) {
    return children;
  }

  if (shouldBypassComingSoon(pathname)) {
    return children;
  }

  if (unlocked) {
    return children;
  }

  if (enabled) {
    return <ComingSoonPage onUnlock={unlockWithCode} />;
  }

  if (loading) {
    return null;
  }

  return children;
}
